import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { ShopProvider } from "@/context/ShopContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductList from "@/pages/ProductList";
import ProductDetail from "@/pages/ProductDetail";
import Collections from "@/pages/Collections";
import Checkout from "@/pages/Checkout";
import { OrderSuccess, OrderCancel } from "@/pages/OrderStatus";
import Bespoke from "@/pages/Bespoke";
import Services from "@/pages/Services";
import Metals from "@/pages/Metals";
import About from "@/pages/About";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Contact from "@/pages/Contact";
import Shipping from "@/pages/Shipping";
import Terms from "@/pages/Terms";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import ResetPassword from "@/pages/ResetPassword";
import MoreInStore from "@/pages/MoreInStore";

const TITLES = [
  [/^\/$/, "Celtic Gold — Jewellery Inspired by the Isle of Man"],
  [/^\/more-in-store/, "More In Store: Silver, Glassware & Gifts — Celtic Gold, Peel"],
  [/^\/shop/, "Shop Jewellery & Gifts — Celtic Gold, Isle of Man"],
  [/^\/collections/, "Collections — Celtic Gold"],
  [/^\/product\//, "Celtic Gold — Peel, Isle of Man"],
  [/^\/bespoke/, "Bespoke Jewellery Isle of Man — Celtic Gold"],
  [/^\/services/, "Jewellery Repairs, Valuations & Engraving — Celtic Gold"],
  [/^\/metals/, "Metal Information — Celtic Gold"],
  [/^\/about/, "About Us — Celtic Gold, Family Jeweller since 1982"],
  [/^\/news/, "News — Celtic Gold"],
  [/^\/contact/, "Contact — Celtic Gold, Peel"],
  [/^\/shipping/, "Delivery & Shipping — Celtic Gold"],
  [/^\/terms/, "Terms & Conditions — Celtic Gold"],
];

const DESCRIPTIONS = [
  [/^\/$/, "Family-run jeweller in Peel, Isle of Man since 1982. Manx, Celtic and Archibald Knox-inspired jewellery, lab-grown diamonds, wedding bands and bespoke commissions."],
  [/^\/more-in-store/, "There's even more in store: silver jewellery, glassware and gifts at our Peel shop — new pieces arriving and selling every week. Pop in and browse."],
  [/^\/shop/, "Shop Celtic, Manx and contemporary jewellery: rings, pendants, bracelets, earrings and charms, hand finished in Peel, Isle of Man."],
  [/^\/collections/, "Explore our collections: Three Legs of Man, Celtic crosses, Archibald Knox, Manx heritage and lab-grown diamonds."],
  [/^\/bespoke/, "Commission a bespoke piece with our goldsmiths in Peel — engagement rings, remodelling and one-off designs made in our Isle of Man workshop."],
  [/^\/services/, "Jewellery repairs, valuations, engraving, ring resizing and watch batteries at Celtic Gold, Peel, Isle of Man."],
  [/^\/metals/, "A guide to precious metals: 9ct and 18ct gold, platinum, palladium and silver — what we use and why at Celtic Gold."],
  [/^\/about/, "Meet the family behind Celtic Gold — handcrafting jewellery in Peel, Isle of Man since 1982."],
  [/^\/news/, "News and updates from Celtic Gold, Peel."],
  [/^\/contact/, "Visit Celtic Gold at 5 Michael Street, Peel, Isle of Man, call 01624 843737 or send us a message — we're always happy to help."],
  [/^\/shipping/, "Delivery and shipping information for Celtic Gold orders — UK, Isle of Man and international."],
  [/^\/terms/, "Terms and conditions for shopping with Celtic Gold Ltd, Peel, Isle of Man."],
];

function setMeta(name, content) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el && content) el.setAttribute("content", content);
}

function ScrollAndTitle() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    const match = TITLES.find(([re]) => re.test(pathname));
    document.title = match ? match[1] : "Celtic Gold — Peel, Isle of Man";
    const desc = DESCRIPTIONS.find(([re]) => re.test(pathname));
    setMeta("description", desc ? desc[1] : "Celtic Gold Ltd — Manx, Celtic and Archibald Knox-inspired jewellery, handcrafted in Peel, Isle of Man since 1982.");
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App min-h-screen bg-ink text-ivory">
      <BrowserRouter>
        <ShopProvider>
          <ScrollAndTitle />
          <Navbar />
          <CartDrawer />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/more-in-store" element={<MoreInStore />} />
            <Route path="/more-in-store/:sub" element={<MoreInStore />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:category" element={<ProductList />} />
            <Route path="/shop/:category/:sub" element={<ProductList />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/collections/:slug" element={<ProductList collectionMode />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order/success" element={<OrderSuccess />} />
            <Route path="/order/cancel" element={<OrderCancel />} />
            <Route path="/bespoke" element={<Bespoke />} />
            <Route path="/services" element={<Services />} />
            <Route path="/metals" element={<Metals />} />
            <Route path="/about" element={<About />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/account" element={<Account />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#121826", border: "1px solid rgba(197,160,89,0.3)", color: "#F9F6F0" } }} />
        </ShopProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
